import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import { ActivityListGroup } from "web_myplann/Activities/Components/ActivityListGroup";
import { ActivityKeys } from "web_myplann/Api/Activities/ActivityKeys";
import { useActivityListQuery } from "web_myplann/Api/Activities/useActivityListQuery";
import { useEventsQuery } from "web_myplann/Api/Activities/useEventsQuery";
import { TaskKeys } from "web_myplann/Api/Tasks/TaskKeys";
import { useTaskListQuery } from "web_myplann/Api/Tasks/useTaskListQuery";
import type {
	ActivityListItemResponseDTO,
	TaskListItemResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { StyledCenteredStack } from "web_myplann/Shared/Components/StyledCenteredStack";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";
import { dateFormatter } from "web_myplann/Shared/Utils/formatters";
import { sortEventsByDateAndTitle } from "web_myplann/Utils/dates";

export const ActivitiesListPage = () => {
	const { t } = useResource();

	const actualDate = dateFormatter(new Date(), "", "yyyy-MM-dd");
	const yesterdayDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");

	const { data: inProgressEvents, isLoading: isLoadingInProgress } =
		useTaskListQuery(undefined, undefined, true);

	const { data: upcomingEvents, isLoading: isLoadingUpcoming } =
		useActivityListQuery(actualDate, undefined, true);

	const { data: overdueEvents, isLoading: isLoadingOverdue } =
		useActivityListQuery(undefined, yesterdayDate, true);

	const { data: resolvedEvents, isLoading: isLoadingResolved } = useEventsQuery(
		undefined,
		undefined,
		undefined,
		true,
	);

	const solvedEventsData = sortEventsByDateAndTitle(
		(inProgressEvents?.data as unknown as TaskListItemResponseDTO[]) ?? [],
		t,
	);
	const upcomingEventsData = sortEventsByDateAndTitle(
		(upcomingEvents?.data as unknown as ActivityListItemResponseDTO[]) ?? [],
		t,
	);
	const overdueEventsData = sortEventsByDateAndTitle(
		(overdueEvents?.data as unknown as ActivityListItemResponseDTO[]) ?? [],
		t,
		"desc",
	);
	const resolvedEventsData = sortEventsByDateAndTitle(
		(resolvedEvents?.data as unknown as ActivityListItemResponseDTO[]) ?? [],
		t,
		"desc",
	);

	const isLoading =
		isLoadingInProgress ||
		isLoadingUpcoming ||
		isLoadingOverdue ||
		isLoadingResolved;

	const allSectionsEmpty =
		!solvedEventsData.length &&
		!upcomingEventsData.length &&
		!overdueEventsData.length &&
		!resolvedEventsData.length &&
		!isLoading;

	//  ToDo: will be used in next phase

	// const [filters] = useAtom(activitiesFiltersState);

	// const filtersActive = useMemo(
	// 	() => !isEqual(filters, defaultActivitiesFiltersState),
	// 	[filters, defaultActivitiesFiltersState],
	// );

	// useEffect(() => {
	// 	if (filtersActive) {
	// 		const newActivities = filterActivities(activities, filters);
	// 		setActivities(newActivities);
	// 	}
	// }, []);

	// const filterActivities = useCallback(
	// 	(
	// 		data: MyPlannActivity[],
	// 		filters: ActivitiesFiltersState,
	// 	): MyPlannActivity[] => {
	// 		let result = data;

	// 		if (filters.dateFrom) {
	// 			result = result.filter(
	// 				(a) =>
	// 					isSameOrAfter(a.dateTo, filters.dateFrom) &&
	// 					isSameOrAfter(a.dateFrom, filters.dateFrom),
	// 			);
	// 		}

	// 		if (filters.dateTo) {
	// 			result = result.filter(
	// 				(a) =>
	// 					isSameOrBefore(a.dateFrom, filters.dateTo) &&
	// 					isSameOrBefore(a.dateTo, filters.dateTo),
	// 			);
	// 		}

	// 		if (filters.types.length > 0) {
	// 			result = result.filter((a) => filters.types.includes(a.type));
	// 		}

	// 		if (filters.created.length > 0) {
	// 			result = result.filter((a) => filters.created.includes(a.created));
	// 		}

	// 		return result;
	// 	},
	// 	[],
	// );

	return (
		<MyPlannLayout
			topBar={<NavigationHeader title={t(Resources.Activity.List.title)} />}
		>
			{isLoading && (
				<StyledCenteredStack spacing={2} px={5} sx={{ zIndex: 10 }}>
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<RowSkeletonLoader />
				</StyledCenteredStack>
			)}

			<Box sx={{ opacity: isLoading ? 0 : 1, transition: "opacity 0.2s" }}>
				<NativePullToRefresh
					queryKeys={[
						TaskKeys.list(),
						[...ActivityKeys.all, "list"],
						ActivityKeys.events(),
					]}
					spinnerOffset={-20}
				>
					{allSectionsEmpty && (
						<EmptyListSkeleton
							label={t(Resources.Activity.List.NoContent)}
							description={t(Resources.Activity.List.NoActivities)}
							data-testid="activities-list-page"
						/>
					)}

					{!allSectionsEmpty && (
						<Stack
							paddingTop={1.6}
							gap={1.6}
							data-testid="activities-list-page"
						>
							{/* ToDo: will be used in next phase */}

							{/* <Filters<MyPlannActivity>
							data={defaultActivities}
							setFilteredData={(value) => {
								const newActivities = filterActivities(value, filters);
								setActivities(newActivities);
							}}
							searchByKey="title"
							active={filtersActive}
							filtersRoute={Routing.ActivitiesFilters.path()}
						/> */}

							<ActivityListGroup
								label={t(Resources.Common.solvedEvents)}
								emptyGroupText={
									upcomingEventsData.length > 0 ||
									overdueEventsData.length > 0 ||
									resolvedEventsData.length > 0
										? t(Resources.Activity.List.NoTask)
										: undefined
								}
								data={solvedEventsData}
							/>
							<ActivityListGroup
								label={t(Resources.Common.upcomingEvents)}
								data={upcomingEventsData}
								emptyGroupText={t(Resources.Activity.List.NoUpcomingEvents)}
							/>
							<ActivityListGroup
								label={t(Resources.Common.overdueEvents)}
								data={overdueEventsData}
								emptyGroupText={t(Resources.Activity.List.NoOverdueEvents)}
							/>
							<ActivityListGroup
								label={t(Resources.Common.resolvedEvents)}
								data={resolvedEventsData}
								emptyGroupText={t(Resources.Activity.List.NoResolvedEvents)}
								noDivider
							/>
						</Stack>
					)}
				</NativePullToRefresh>
			</Box>
		</MyPlannLayout>
	);
};
